import { FieldValues } from 'react-hook-form';

import {
  TDateFieldTriggerProps,
  TDynamicField,
} from '@/shared/components/ui/form/components/dynamic-field-renderer/dynamic-field-renderer.types';
import { FormCheckboxGroup } from '@/shared/components/ui/form/components/form-checkbox-group/form-checkbox-group';
import { FormDatePicker } from '@/shared/components/ui/form/components/form-date-time-picker/variants/form-date-picker';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { TFormSelectOption } from '@/shared/components/ui/form/components/form-select/form-select.types';
import { FormToggle } from '@/shared/components/ui/form/components/form-toggle/form-toggle';
import { RadioGroup } from '@/shared/components/ui/form/components/radio-group/radio-group';
import { TDefaultValue } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { TextField } from '@/shared/components/ui/form/components/text-field/text-field';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';

const asDefaultValue = <TFieldValues extends FieldValues>(value: unknown) =>
  value as TDefaultValue<TFieldValues>;

const asSelectOptions = <TFieldValues extends FieldValues>(
  options: TDynamicField['options'],
) => (options ?? []) as unknown as TFormSelectOption<TFieldValues>[];

export const DateFieldTrigger = ({
  value,
  ...rest
}: TDateFieldTriggerProps) => {
  const { formatLocalTime, dateTimeFormat } = useDateUtils();
  const dateOnlyFormat = dateTimeFormat.split(' ')[0];

  return (
    <TextField
      hideOptionalLabel
      InputProps={{ readOnly: true }}
      value={
        value
          ? formatLocalTime(`${value}T00:00:00Z`, 'UTC', dateOnlyFormat)
          : ''
      }
      {...rest}
    />
  );
};

export const DynamicFieldRenderer = <TFieldValues extends FieldValues>({
  field,
}: {
  field: TDynamicField<TFieldValues>;
}) => {
  if (field.kind === 'toggle') {
    return (
      <FormToggle<TFieldValues>
        name={field.name}
        label={field.label}
        required={field.required}
        defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
        leftLabel={field.toggleLeftLabel}
        rightLabel={field.toggleRightLabel}
        helperText={field.helperText}
        hint={field.hint}
      />
    );
  }

  if (field.kind === 'select') {
    return (
      <FormSelect<TFieldValues>
        name={field.name}
        label={field.label}
        required={field.required}
        defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
        placeholder={field.placeholder}
        hint={field.hint}
        options={asSelectOptions<TFieldValues>(field.options)}
      />
    );
  }

  if (field.kind === 'multiSelect') {
    return (
      <FormCheckboxGroup<TFieldValues>
        name={field.name}
        label={field.label}
        required={field.required}
        defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
        options={field.options ?? []}
      />
    );
  }

  if (field.kind === 'radio') {
    return (
      <RadioGroup<TFieldValues>
        name={field.name}
        label={field.label}
        required={field.required}
        defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
        hint={field.hint}
        options={field.options ?? []}
        optionDirection="column"
      />
    );
  }

  if (field.kind === 'date') {
    return (
      <FormDatePicker<TFieldValues>
        name={field.name}
        label={field.label}
        required={field.required}
        defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
        hint={field.hint}
        dateOnly
        triggerElement={(value) => <DateFieldTrigger value={value} />}
      />
    );
  }

  return (
    <FormInput<TFieldValues>
      name={field.name}
      label={field.label}
      required={field.required}
      defaultValue={asDefaultValue<TFieldValues>(field.defaultValue)}
      placeholder={field.placeholder}
      hint={field.hint}
      multiline={field.multiline}
      type={field.kind === 'number' ? 'number' : undefined}
    />
  );
};
